// Signals learning loop — human review queue API client.
// Backend: app/routes/signals_review.py under /api/admin/signals/review.

import api from './api';

export type ReviewEntryType = 'needs_human_signal' | 'skip_audit';
export type ReviewStatus = 'pending' | 'confirmed' | 'corrected' | 'rejected';
export type LessonStatus = 'not_started' | 'running' | 'written' | 'failed';
export type ReviewDecision = 'confirm' | 'correct' | 'reject';

export interface BridgeKeys {
  customer_no?: string | null;
  account_id?: string | null;
  order_id?: string | null;
  sage_so_no?: string | null;
  sku?: string[];
  batch_id?: string | null;
}

// The kb_signals candidate shape (pipeline proposal / human correction).
export interface ProposedEntry {
  observation: string;
  significance?: string;
  themes?: string[];
  products?: string[];
  context?: string;
  observed_at?: string | null;
  observed_by?: string | null;
  confidence?: number;
  bridge_keys?: BridgeKeys;
  note?: string;
}

// Why the row is (still) in front of a human — the queue's reason chip.
export type QueueReason =
  | 'conflict'
  | 'possible_duplicate'
  | 'customer_unresolved'
  | 'customer_ambiguous'
  | 'demoted_bind'
  | 'ambiguous_keys'
  | 'other';

export interface ReviewListItem {
  id: string;
  entry_type: ReviewEntryType;
  status: ReviewStatus;
  email_id: string;
  mailbox: string;
  email_subject: string | null;
  from_address: string | null;
  source_table: string;
  overturn_category: string | null;
  proposed_entry: ProposedEntry;
  lesson_status: LessonStatus;
  created_at: string;
  updated_at: string;
  queue_reason?: QueueReason | null;
  // 'auto' for confidence-gated auto rulings, else the human account.
  decided_by?: string | null;
}

export interface ReviewListResponse {
  items: ReviewListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface HumanDecision {
  decision: ReviewDecision;
  corrected_entry: ProposedEntry | null;
  assertions?: Record<string, AssertionDecision> | null;
  reason: string | null;
  decided_by: string;
  decided_at: string;
}

// One reviewable claim, derived server-side at read time from the proposed
// entry + resolver traces (services/signals_review/assertions.py).
export type AssertionEvidenceStatus = 'confident' | 'disputed';
export type AssertionVerdict = 'confirm' | 'correct';

export interface ReviewAssertion {
  id: string; // is_signal | customer_no | sku:<CODE> | order_id | ...
  label: string;
  claimed_value: string | boolean | null;
  evidence_status: AssertionEvidenceStatus;
  evidence_note: string;
}

export interface AssertionDecision {
  verdict: AssertionVerdict;
  corrected_value?: string | null;
}

export interface ReviewDetail extends ReviewListItem {
  resolution_traces: Record<string, unknown>[];
  human_decision: HumanDecision | null;
  kb_signals_entry_id: string | null;
  resolution_kb_entry_id: string | null;
  lesson_error: string | null;
  harness_candidate: Record<string, unknown> | null;
  assertions: ReviewAssertion[];
}

export interface DecideRequest {
  decision: ReviewDecision;
  corrected_entry?: ProposedEntry;
  reason?: string;
  // Per-assertion rulings keyed by assertion id; must be consistent with the
  // entry-level decision (the API 422s otherwise).
  assertions?: Record<string, AssertionDecision>;
}

export interface DecideResponse {
  item_id: string;
  status: ReviewStatus;
  lesson_dispatched: boolean;
}

const BASE = '/admin/signals/review';

export const signalsReviewApi = {
  list: async (status?: ReviewStatus): Promise<ReviewListResponse> => {
    const r = await api.get<ReviewListResponse>(BASE, {
      params: status ? { status } : undefined,
    });
    return r.data;
  },
  get: async (id: string): Promise<ReviewDetail> => {
    const r = await api.get<ReviewDetail>(`${BASE}/${id}`);
    return r.data;
  },
  decide: async (id: string, body: DecideRequest): Promise<DecideResponse> => {
    const r = await api.post<DecideResponse>(`${BASE}/${id}/decide`, body);
    return r.data;
  },
};
